import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import styles from "./Input.module.scss";

/**
 * Searchable Select component with dropdown filtering
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - Label text
 * @param {Array} props.options - Select options array
 * @param {boolean} props.required - Whether the select is required
 * @param {string} props.name - Select name
 * @param {string} props.placeholder - Placeholder text
 * @param {function} props.onChange - Change handler
 * @param {string} props.value - Controlled select value 
 * @param {boolean} props.floating - Whether to use floating label design
 * @param {string} props.className - Additional CSS classes
 */
const SearchableSelect = forwardRef((props, ref) => {
	const {
		label,
		options = [],
		required,
		name,
		placeholder = "Bitte auswählen...",
		onChange,
		value,
		className = "",
		floating = true,
		error,
		...rest
	} = props;

	const [searchValue, setSearchValue] = useState("");
	const [filteredOptions, setFilteredOptions] = useState(options);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [selectedLabel, setSelectedLabel] = useState("");
	const selectRef = useRef(null);
	const dropdownRef = useRef(null);
	const hiddenInputRef = useRef(null);

	useImperativeHandle(ref, () => ({
		reset: () => {
			setSearchValue("");
			setSelectedLabel("");
			if (onChange) {
				onChange({
					target: {
						name,
						value: ""
					}
				});
			}
		}
	}));

	// Effect to set the selected label based on the value
	useEffect(() => {
		if (value) {
			const option = options.find(opt => opt.value === value);
			if (option) {
				setSelectedLabel(option.label);
			}
		} else {
			setSelectedLabel("");
		}
	}, [value, options]);

	// Effect to filter options based on search input
	useEffect(() => {
		if (!searchValue.trim()) {
			setFilteredOptions(options);
		} else {
			const filtered = options.filter(option =>
				option.label.toLowerCase().includes(searchValue.toLowerCase())
			);
			setFilteredOptions(filtered);
		}
	}, [searchValue, options]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
				selectRef.current && !selectRef.current.contains(event.target)) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const handleSearchChange = (e) => {
		setSearchValue(e.target.value);
	};

	const handleToggleDropdown = () => {
		setIsDropdownOpen(!isDropdownOpen);
	};

	const handleOptionClick = (option) => {
		setSelectedLabel(option.label);
		setSearchValue("");
		setIsDropdownOpen(false);
		

		if (onChange) {
			onChange({
				target: {
					name,
					value: option.value || option.option || option.label
				}
			});
		}

		if (hiddenInputRef.current) {
			hiddenInputRef.current.value = option.value || option.option || option.label
			hiddenInputRef.current.dispatchEvent(new Event('input', { bubbles: true })); 
			hiddenInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
		}
	};

	// Standard (non-floating) searchable select with label
	if (!floating) {
		return (
			<div className={`${className} mb-3 position-relative`}>
				{label && (
					<label 
						htmlFor={rest.id || name} 
						className="form-label">
						{label}
						{required && <span className="text-danger ms-1">*</span>}
					</label>
				)}
				<div
					className={`form-control form-select d-flex align-items-center justify-content-between ${error ? 'is-invalid' : ''}`}
					ref={selectRef}
					onClick={handleToggleDropdown}
					role="button"
					tabIndex={0}
					aria-expanded={isDropdownOpen}
					aria-controls={`dropdown-${name}`}>
					<input
					type="hidden"
					name={name}
					ref={hiddenInputRef} 
					{...(value !== undefined ? { value } : { value: selectedLabel || "" })}
					data-field={name}
					required={required}
					{...rest}
					/>
					<div className="flex-grow-1">
						{selectedLabel || <span className="text-muted">{placeholder}</span>}
					</div>
					<span className="ms-2">
						<i className={`bi bi-chevron-${isDropdownOpen ? 'up' : 'down'}`}></i>
					</span>
				</div>
				{isDropdownOpen && (
					<div 
						ref={dropdownRef} 
						className="position-absolute start-0 w-100 bg-white border rounded shadow-sm mt-1 z-1" 
						style={{ maxHeight: '250px', overflowY: 'auto', zIndex: 1000 }}
						id={`dropdown-${name}`}>
						<div className="p-2 border-bottom">
							<input
								type="text"
								className="form-control form-control-sm"
								placeholder="Suchen ..."
								value={searchValue}
								onChange={handleSearchChange}
								onClick={(e) => e.stopPropagation()}
								data-field={`${name}-search`}
							/>
						</div>
						{filteredOptions.length > 0 ? (
							<div>
								{filteredOptions.map((option, index) => (
									<div
										key={option.value || index}
										className={`p-2 cursor-pointer hover-bg-light ${value === option.value ? 'bg-light' : ''}`}
										onClick={() => handleOptionClick(option)}
										role="option"
										aria-selected={value === option.value}>
										{option.label}
									</div>
								))}
							</div>
						) : (
							<div className="p-2 text-muted">No options found</div>
						)}
					</div>
				)}
				
				{error && <div className="invalid-feedback">{error}</div>}
			</div>
		);
	}

	// Floating label searchable select (original implementation)
	return (
		<div className={className}>
			<div className={`form-floating mb-3 ${styles.searchableSelect}`} >

				<div
					className={`form-select ${value ? 'has-value' : ''} ${error ? 'is-invalid' : ''}`}
					id={rest.id || name}
					ref={selectRef}
					onClick={handleToggleDropdown}
					role="button"
					tabIndex={0}
					aria-expanded={isDropdownOpen}
					aria-controls={`dropdown-${name}`}>

					{selectedLabel || <span>{placeholder}&nbsp;</span>}

					<input
					type="hidden"
					name={name}
					ref={hiddenInputRef} 
					{...(value !== undefined ? { value } : { value: selectedLabel || "" })}
					data-field={name}
					required={required}
					{...rest}
					/>
				</div>
				<label 
					htmlFor={rest.id || name}
					className={value ? 'focused' : ''}>
					{label}
				</label>
				{isDropdownOpen && (
					<div 
						ref={dropdownRef} 
						className="position-absolute start-0 w-100 bg-white border rounded shadow-sm mt-1 z-1" 
						style={{ maxHeight: '250px', overflowY: 'auto', zIndex: 1000 }}
						id={`dropdown-${name}`}>
						<div className="p-2 border-bottom">
							<input
								type="text"
								className="form-control form-control-sm"
								placeholder="Suchen ..."
								value={searchValue}
								onChange={handleSearchChange}
								onClick={(e) => e.stopPropagation()}
								data-field={`${name}-search`}
							/>
						</div>
						{filteredOptions.length > 0 ? (
							<div>
								{filteredOptions.map((option, index) => (
									<div
										key={option.value || index}
										className={`p-2 cursor-pointer hover-bg-light ${value === option.value ? 'bg-light' : ''}`}
										onClick={() => handleOptionClick(option)}
										role="option"
										aria-selected={value === option.value}>
										{option.label}
									</div>
								))}
							</div>
						) : (
							<div className="p-2 text-muted">No options found</div>
						)}
					</div>
				)}
				
			</div>
			{error && <div className="invalid-feedback d-block">{error}</div>}
		</div>
	);
});

SearchableSelect.displayName = 'SearchableSelect';

export default SearchableSelect;
